import React from 'react';
import { Checkbox, Modal, Switch, Tooltip } from 'antd';
import LastResult from './LastResult';
import { intervalOffset } from '../../util/collectorHelpers';
import { Flex, useAssetTranslation } from '@mss/mss-component-library';
import dateFormat from 'dateformat';
import { TFunction } from 'i18next';
import { formatExtraCategories } from './ActionMenu';

const hasConfigPermission = (
  collector_key: string,
  has_permission: boolean | undefined
) => {
  if (collector_key === 'network_config') {
    return has_permission;
  }
  return true;
};

const columns = (
  update: (opt: any) => Promise<any> | any,
  ActionMenu: any,
  collectorType: string,
  t: TFunction,
  canEdit?: boolean,
  canEditNetworkConfig?: boolean,
  canViewNetworkConfig?: boolean,
  collectorsPreset?: any,
  disableEnabledCheck?: boolean
) => {
  return [
    {
      title: t('Collector'),
      dataIndex: 'label',
      key: 'label',
    },
    {
      title: t('Last Run'),
      dataIndex: 'last_run_str',
      key: 'last_run_str',
      width: 131,
      render: (last_run_str: string, { last_run_at }: any) => {
        const lastRunDate = new Date(last_run_at);
        const lastRunStr = new Date(
          lastRunDate.getTime() - lastRunDate.getTimezoneOffset() * 60000
        ).toString();

        if (last_run_str)
          return (
            <Tooltip
              title={dateFormat(lastRunStr || '', 'mmmm dS, yyyy @ h:MM:ss TT')}
            >
              <span>{last_run_str}</span>
            </Tooltip>
          );
        return <span>-</span>;
      },
    },
    {
      title: t('Next Run'),
      dataIndex: 'next_run_str',
      key: 'next_run_str',
      width: 131,
      render: (next_run_str: string, { next_run_at }: any) => {
        const nextRunDate = new Date(next_run_at);
        const nextRunStr = new Date(
          nextRunDate.getTime() - nextRunDate.getTimezoneOffset() * 60000
        ).toString();
        if (next_run_str)
          return (
            <Tooltip
              title={dateFormat(nextRunStr || '', 'mmmm dS, yyyy @ h:MM:ss TT')}
            >
              <span>{next_run_str}</span>
            </Tooltip>
          );
        return <span>-</span>;
      },
    },
    {
      title: t('Last Result'),
      dataIndex: 'last_result_display',
      key: 'last_result_display',
      width: 116,
      render: (lastResult: string, collector: any) => (
        <Flex
          data-ta={`lastResult-${collector.collector_type}.${collector.collector_key}`}
        >
          <LastResult status={lastResult} />
        </Flex>
      ),
    },
    {
      title: t('Interval'),
      dataIndex: 'interval',
      key: 'interval',
      width: 105,
      render: (interval: number) => {
        const offsetUnit = intervalOffset(interval);
        const hoursTimeUnit = interval / 60 === 1 ? 'hour' : 'hours';
        return (
          interval > 0 && (
            <span>
              {offsetUnit === 'hours' ? interval / 60 : interval}{' '}
              {offsetUnit === 'hours' ? hoursTimeUnit : offsetUnit}
            </span>
          )
        );
      },
    },
    {
      title: t('Status'),
      dataIndex: 'enabled',
      key: 'enabled',
      width: 50,
      render: (enabled: boolean, collector: any) => {
        const updater = (enabled: boolean, extraOptions?: string[]) =>
          update({
            ...collector,
            ...(extraOptions
              ? { extra_options: { filter_categories: extraOptions } }
              : {}),
            enabled,
          });
        return (
          <ToggleColumn
            collector={collector}
            collectorsPreset={collectorsPreset}
            canEdit={canEdit}
            canEditNetworkConfig={canEditNetworkConfig}
            enabled={enabled}
            disableEnabledCheck={disableEnabledCheck}
            updater={updater}
          />
        );
      },
    },
    {
      title: '',
      key: 'actions',
      width: 16,
      render: (id: any, collector: any) => (
        <div
          data-ta={`menu-${collector.collector_type}.${collector.collector_key}`}
        >
          {canEdit && collector.intervals && (
            <ActionMenu
              collector={collector}
              update={update}
              canUpdate={hasConfigPermission(
                collector.collector_key,
                canEditNetworkConfig
              )}
              canView={hasConfigPermission(
                collector.collector_key,
                canViewNetworkConfig
              )}
              collectorType={collectorType}
              collectorsPreset={collectorsPreset}
            />
          )}
        </div>
      ),
    },
  ];
};
export default columns;

const ToggleColumn = ({
  collector,
  collectorsPreset,
  canEdit,
  canEditNetworkConfig,
  enabled,
  disableEnabledCheck,
  updater,
}: any) => {
  const extraOptions = ['network_config', 'historical_messages'].includes(
    collector.collector_key
  )
    ? collectorsPreset?.data?.[collector.collector_type]?.extra_options
    : undefined;

  const hasAnyExtraOptionsEnabled =
    collector.extra_options?.filter_categories?.length;
  const { t } = useAssetTranslation();
  const [newExtraOptions, setNewExtraOptions] = React.useState<
    string[] | undefined
  >(undefined);
  const [showModal, setShowModal] = React.useState<boolean>(false);

  const hasPermission =
    canEdit &&
    hasConfigPermission(collector.collector_key, canEditNetworkConfig);

  return (
    <Flex
      h="center"
      v="center"
      data-ta={`${collector.collector_type}.${collector.collector_key}`}
    >
      <Switch
        checked={enabled}
        size="small"
        disabled={
          !(
            hasPermission &&
            (disableEnabledCheck || collector.defaultInterval !== -1)
          )
        }
        onChange={
          hasPermission
            ? (checked: boolean) =>
                !hasAnyExtraOptionsEnabled && extraOptions?.length && checked
                  ? setShowModal(true)
                  : updater(checked)
            : () => {}
        }
      />
      {extraOptions && (
        <Modal
          title={t('Select extra options')}
          okText={
            newExtraOptions
              ? t('Enable collector')
              : t('Enable collector without options')
          }
          open={showModal}
          onOk={() => {
            setShowModal(false);
            updater(true, newExtraOptions);
            setNewExtraOptions(undefined);
          }}
          onCancel={() => {
            setShowModal(false);
            setNewExtraOptions(undefined);
          }}
          destroyOnClose
        >
          <p>
            {t(
              'Please select the following data endpoints you want to collect data from. Keep in mind that if no endpoints are selected, no actual data will be collected.'
            )}
          </p>
          <ul className="ml-4">
            {extraOptions.map((option: string) => (
              <li key={option}>
                <Checkbox
                  checked={newExtraOptions?.includes(option)}
                  onChange={checked =>
                    setNewExtraOptions((prev = []) =>
                      checked.target.checked
                        ? [...prev, option]
                        : prev.length < 2
                          ? undefined
                          : prev.filter(x => x !== option)
                    )
                  }
                >
                  {formatExtraCategories(option, collector.collector_key)}
                </Checkbox>
              </li>
            ))}
          </ul>
        </Modal>
      )}
    </Flex>
  );
};
